import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import { Button, Select } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { usePermissions } from '@hooks';
import { Dialog } from '@molecules';
import { Area } from '@modules/clients/modules/CompanyDetails/components/AreasList/AreasList';

interface AssignAreaModalProps {
  areas: Area[];
  postAssignArea: (area: string) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  description?: string;
}
/**
 * Renders a modal with an areas selector
 * @param props
 *  @param props.areas areas available to assign
 *  @param props.postAssignArea function to assign the selected area
 *  @param props.isOpen whether the modal is open or not
 *  @param props.onClose function to close the modal
 *  @param props.isLoading boolean to indicate if the area assignment is in progress
 */
const AssignAreaModal = ({
  postAssignArea,
  isOpen,
  onClose,
  isLoading,
  areas,
  description,
}: AssignAreaModalProps) => {
  const { t } = useTranslation();
  const [selectedArea, setSelectedArea] = useState('');
  const isDemo = usePermissions([PERMISSIONS.demoUpdateArea]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        dialogTitle={
          <Box>
            <Typography variant='h4'>{t('EMPLOYEES:ASSIGN_AREA_MODAL:MODAL_TITLE')}</Typography>
            <Typography variant='body1'>
              {description ? t(description) : t('EMPLOYEES:ASSIGN_AREA_MODAL:MODAL_DESCRIPTION')}
            </Typography>
          </Box>
        }
        dialogContent={
          <Select
            name={'AREA'}
            label={t('COMMON:AREA')}
            value={selectedArea}
            onChange={(e: SelectChangeEvent<string>) => setSelectedArea(e.target.value)}
            parseOption={(item: Area) => ({ label: item.fantasyName, value: item.name })}
            items={areas}
          />
        }
        dialogActions={
          <>
            <Button
              text={t('COMMON:CANCEL')}
              color='error'
              onClick={onClose}
              disabled={isLoading}
            />
            <Button
              variant='contained'
              color='primary'
              type='submit'
              isLoading={isLoading}
              disabled={!selectedArea || isDemo}
              onClick={() => postAssignArea(selectedArea)}
              text={t('COMMON:CONFIRM')}
            />
          </>
        }
      ></Dialog>
    </>
  );
};

export default AssignAreaModal;
